import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Search, Filter, X } from 'lucide-react';

interface OrderFiltersProps {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    statusFilter: string;
    setStatusFilter: (status: string) => void;
}

export const OrderFilters: React.FC<OrderFiltersProps> = ({
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter
}) => {
    const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);

    const hasActiveFilters = statusFilter !== 'all' || searchTerm.length > 0;

    const clearFilters = () => {
        setStatusFilter('all');
        setSearchTerm('');
    };

    return (
        <>
            {/* Mobile: Compact search + filter button */}
            <div className="flex gap-2 mb-4 md:hidden">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                        placeholder="Search orders..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 pr-8 h-10 bg-white"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
                    <SheetTrigger asChild>
                        <Button variant="outline" size="icon" className="h-10 w-10 relative flex-shrink-0">
                            <Filter className="h-4 w-4" />
                            {hasActiveFilters && (
                                <span className="absolute -top-1 -right-1 h-3 w-3 bg-blue-600 rounded-full" />
                            )}
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="h-auto max-h-[50vh] rounded-t-2xl">
                        <SheetHeader className="pb-4">
                            <SheetTitle className="flex items-center justify-between">
                                <span>Filter Orders</span>
                                {hasActiveFilters && (
                                    <Button variant="ghost" size="sm" onClick={clearFilters} className="text-blue-600 h-8">
                                        Clear all
                                    </Button>
                                )}
                            </SheetTitle>
                        </SheetHeader>
                        <div className="space-y-4 pb-6">
                            <div>
                                <label className="text-sm font-medium text-gray-700 mb-2 block">Status</label>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        { value: 'all', label: 'All' },
                                        { value: 'pending', label: 'Pending' },
                                        { value: 'confirmed', label: 'Confirmed' },
                                        { value: 'preparing', label: 'Preparing' },
                                        { value: 'ready', label: 'Ready' },
                                        { value: 'completed', label: 'Completed' },
                                        { value: 'cancelled', label: 'Cancelled' },
                                    ].map((status) => (
                                        <Button
                                            key={status.value}
                                            variant={statusFilter === status.value ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => {
                                                setStatusFilter(status.value);
                                                setIsFilterSheetOpen(false);
                                            }}
                                            className="h-9"
                                        >
                                            {status.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>

            {/* Active filter indicator on mobile */}
            {hasActiveFilters && (
                <div className="flex gap-2 mb-3 md:hidden overflow-x-auto pb-1">
                    {statusFilter !== 'all' && (
                        <Badge variant="secondary" className="flex items-center gap-1 flex-shrink-0">
                            Status: {statusFilter}
                            <button onClick={() => setStatusFilter('all')}>
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    )}
                </div>
            )}

            {/* Desktop: Original inline filters */}
            <div className="hidden md:flex md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                        placeholder="Search by order #, customer name, or table..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white"
                    />
                </div>

                <div className="w-48">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="bg-white">
                            <div className="flex items-center gap-2">
                                <Filter className="h-4 w-4 text-gray-500" />
                                <SelectValue placeholder="Filter Status" />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="preparing">Preparing</SelectItem>
                            <SelectItem value="ready">Ready</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </>
    );
};
